import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBadge from '../components/StatusBadge';

interface Payslip {
  id: string;
  payslip_number: string;
  employee_code: string;
  first_name: string;
  last_name: string;
  payslip_month: string;
  net_pay: number;
  payment_status: string;
  published?: boolean;
}

interface PayrollRun {
  id: string;
  payroll_number: string;
  payroll_month: string;
  total_employees: number;
  processed_employees: number;
  total_net_pay: number;
  status: string;
}

const ADVANCE_FIELD_LABELS: Record<string, string> = {
  basicSalary: 'Basic Salary',
  otPay: 'Overtime Pay',
  bonus: 'Bonus',
  allowances: 'Allowances',
  owAmount: 'Ordinary Wage (OW)',
  awAmount: 'Additional Wage (AW)',
  cpfEmployee: 'CPF (Employee)',
  funds: 'Community Funds',
  fwl: 'Foreign Worker Levy',
  nsPay: 'NS Pay',
  deductions: 'Deductions',
  totalDeductions: 'Total Deductions',
  cpfEmployer: 'CPF (Employer)',
  sdl: 'SDL',
  payslipMessage: 'Payslip Message',
  bottomMessage: 'Bottom Message',
};

export default function Payslips() {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState<(Payslip & Record<string, any>) | null>(null);
  const [detailComponents, setDetailComponents] = useState<any[]>([]);
  const [showRunForm, setShowRunForm] = useState(false);
  const [processTarget, setProcessTarget] = useState<PayrollRun | null>(null);
  const [processing, setProcessing] = useState(false);
  const [runForm, setRunForm] = useState({ payrollMonth: '', payrollPeriodStart: '', payrollPeriodEnd: '', payPeriod: 'END' });
  const [selectedPayslips, setSelectedPayslips] = useState<string[]>([]);
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [showAdvanceSettings, setShowAdvanceSettings] = useState(false);
  const [advanceFields, setAdvanceFields] = useState<{ key: string; visible: boolean }[]>([]);
  const [savingAdvanceSettings, setSavingAdvanceSettings] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPayslips();
    fetchRuns();
  }, []);

  const openAdvanceSettings = async () => {
    try {
      const res = await api.get('/payroll/payslips/advance-settings');
      setAdvanceFields(res.data.fields || []);
      setShowAdvanceSettings(true);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to load Advance Settings');
    }
  };

  const toggleAdvanceField = (key: string) =>
    setAdvanceFields((prev) => prev.map((f) => (f.key === key ? { ...f, visible: !f.visible } : f)));

  const saveAdvanceSettings = async () => {
    setSavingAdvanceSettings(true);
    try {
      const fields = Object.fromEntries(advanceFields.map((f) => [f.key, f.visible]));
      await api.put('/payroll/payslips/advance-settings', { fields });
      setShowAdvanceSettings(false);
      flash('Advance Settings saved');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save Advance Settings');
    } finally {
      setSavingAdvanceSettings(false);
    }
  };

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payroll/payslips');
      setPayslips(response.data.payslips || []);
    } catch (error) {
      console.error('Failed to fetch payslips:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRuns = async () => {
    try {
      const res = await api.get('/payroll/runs');
      setRuns(res.data.runs || []);
    } catch (error) {
      console.error('Failed to fetch payroll runs:', error);
    }
  };

  const handleCreateRun = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/payroll/runs', runForm);
      setRunForm({ payrollMonth: '', payrollPeriodStart: '', payrollPeriodEnd: '', payPeriod: 'END' });
      setShowRunForm(false);
      fetchRuns();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create payroll run');
    }
  };

  const handleProcess = async () => {
    if (!processTarget) return;
    setProcessing(true);
    try {
      const res = await api.post(`/payroll/runs/${processTarget.id}/process`);
      alert(
        `Processed ${res.data.result.processed}/${res.data.result.totalEmployees} employees. ` +
          `${res.data.result.errors} error(s).` +
          (res.data.result.errors > 0
            ? `\n\n${res.data.result.errorDetails.map((e: any) => e.message).join('\n')}`
            : '')
      );
      setProcessTarget(null);
      fetchRuns();
      fetchPayslips();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to process payroll run');
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelect = (id: string) =>
    setSelectedPayslips((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  const flash = (msg: string) => {
    setDeliveryMessage(msg);
    setTimeout(() => setDeliveryMessage(''), 4000);
  };

  const printSelected = async () => {
    if (!selectedPayslips.length) return;
    try {
      if (selectedPayslips.length === 1) {
        const res = await api.post('/payroll/payslips/print', { payslipIds: selectedPayslips }, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
      } else {
        const res = await api.post('/payroll/payslips/print', { payslipIds: selectedPayslips });
        flash(res.data.message);
      }
      fetchPayslips();
    } catch (error: any) {
      flash(error.response?.data?.message || 'Failed to print');
    }
  };

  const emailSelected = async () => {
    if (!selectedPayslips.length) return;
    try {
      const res = await api.post('/payroll/payslips/email', { payslipIds: selectedPayslips });
      flash(res.data.message);
      fetchPayslips();
    } catch (error: any) {
      flash(error.response?.data?.message || 'Failed to email');
    }
  };

  const publishSelected = async () => {
    if (!selectedPayslips.length) return;
    try {
      const res = await api.post('/payroll/payslips/publish', { payslipIds: selectedPayslips });
      flash(res.data.message);
      setSelectedPayslips([]);
      fetchPayslips();
    } catch (error: any) {
      flash(error.response?.data?.message || 'Failed to publish');
    }
  };

  const openDetail = async (payslip: Payslip) => {
    try {
      const res = await api.get(`/payroll/payslips/${payslip.id}`);
      setDetailTarget({ ...payslip, ...res.data.payslip });
      setDetailComponents(res.data.components || []);
    } catch (error) {
      alert('Failed to load payslip detail');
    }
  };

  const runColumns: Column<PayrollRun>[] = [
    { key: 'payroll_number', header: 'Run No.' },
    {
      key: 'payroll_month',
      header: 'Month',
      render: (row) => new Date(row.payroll_month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    },
    { key: 'processed_employees', header: 'Processed', render: (row) => `${row.processed_employees ?? 0}/${row.total_employees ?? 0}` },
    { key: 'total_net_pay', header: 'Total Net Pay', render: (row) => `$${Number(row.total_net_pay || 0).toLocaleString()}` },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-3">
          <button onClick={() => navigate(`/payroll/runs/${row.id}/cycle`)} className="text-blue-600 hover:underline text-sm font-medium">
            Open Cycle
          </button>
          {row.status === 'DRAFT' && (
            <button onClick={() => setProcessTarget(row)} className="text-blue-600 hover:underline text-sm font-medium">
              Process
            </button>
          )}
        </div>
      ),
    },
  ];

  const payslipColumns: Column<Payslip>[] = [
    {
      key: 'select',
      header: '',
      render: (row) => (
        <input type="checkbox" checked={selectedPayslips.includes(row.id)} onChange={() => toggleSelect(row.id)} />
      ),
    },
    { key: 'payslip_number', header: 'Payslip No.' },
    { key: 'employee', header: 'Employee', render: (row) => `${row.first_name} ${row.last_name}` },
    {
      key: 'payslip_month',
      header: 'Month',
      render: (row) => new Date(row.payslip_month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    },
    { key: 'net_pay', header: 'Net Pay', render: (row) => `$${Number(row.net_pay).toLocaleString()}` },
    { key: 'payment_status', header: 'Status', render: (row) => <StatusBadge status={row.payment_status} /> },
    { key: 'published', header: 'Published', render: (row) => (row.published ? <StatusBadge status="PUBLISHED" /> : <span className="text-gray-400 text-sm">No</span>) },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <button onClick={() => openDetail(row)} className="text-blue-600 hover:underline text-sm">
          View Detail
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-8">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Payroll Runs</h1>
          <Button onClick={() => setShowRunForm(!showRunForm)}>{showRunForm ? 'Cancel' : 'Create Run'}</Button>
        </div>

        {showRunForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <form onSubmit={handleCreateRun} className="grid grid-cols-3 gap-4">
              <Input
                label="Payroll Month"
                type="month"
                value={runForm.payrollMonth.slice(0, 7)}
                onChange={(e) => setRunForm({ ...runForm, payrollMonth: e.target.value + '-01' })}
                required
              />
              <Input
                label="Period Start"
                type="date"
                value={runForm.payrollPeriodStart}
                onChange={(e) => setRunForm({ ...runForm, payrollPeriodStart: e.target.value })}
                required
              />
              <Input
                label="Period End"
                type="date"
                value={runForm.payrollPeriodEnd}
                onChange={(e) => setRunForm({ ...runForm, payrollPeriodEnd: e.target.value })}
                required
              />
              <Select
                label="Pay Period"
                value={runForm.payPeriod}
                onChange={(e) => setRunForm({ ...runForm, payPeriod: e.target.value })}
                options={[
                  { value: 'END', label: 'End of Month' },
                  { value: 'MID', label: 'Mid-Month' },
                  { value: 'BONUS', label: 'Bonus' },
                ]}
              />
              <Button type="submit" className="col-span-3">
                Create Draft Run
              </Button>
            </form>
          </div>
        )}

        <Table columns={runColumns} rows={runs} emptyMessage="No payroll runs yet" />
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Payslips</h1>
          <div className="flex gap-2 items-center">
            {deliveryMessage && <span className="text-sm text-green-700">{deliveryMessage}</span>}
            <Button variant="secondary" onClick={openAdvanceSettings}>
              Advance Settings
            </Button>
            <Button variant="secondary" onClick={printSelected} disabled={!selectedPayslips.length}>
              Print
            </Button>
            <Button variant="secondary" onClick={emailSelected} disabled={!selectedPayslips.length}>
              Email
            </Button>
            <Button onClick={publishSelected} disabled={!selectedPayslips.length}>
              Publish
            </Button>
            <Button variant="secondary" onClick={fetchPayslips}>
              Refresh
            </Button>
          </div>
        </div>
        <Table columns={payslipColumns} rows={payslips} loading={loading} emptyMessage="No payslips available" />
      </div>

      <ConfirmDialog
        isOpen={!!processTarget}
        title="Process Payroll Run"
        message={`This will calculate CPF, SDL, overtime pay, and loan deductions for every active employee in ${processTarget?.payroll_number}, and generate real payslips. This cannot be easily undone. Continue?`}
        confirmLabel="Process Payroll"
        variant="danger"
        loading={processing}
        onConfirm={handleProcess}
        onCancel={() => setProcessTarget(null)}
      />

      <Modal isOpen={showAdvanceSettings} onClose={() => setShowAdvanceSettings(false)} title="Advance Settings — Payslip Layout">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Show/hide fields on the payslip layout. Applies to Print, Email, and Publish (ESS view).
          </p>
          <div className="grid grid-cols-2 gap-2">
            {advanceFields.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.visible} onChange={() => toggleAdvanceField(f.key)} />
                {ADVANCE_FIELD_LABELS[f.key] || f.key}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowAdvanceSettings(false)}>
              Cancel
            </Button>
            <Button onClick={saveAdvanceSettings} disabled={savingAdvanceSettings}>
              {savingAdvanceSettings ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!detailTarget} onClose={() => setDetailTarget(null)} title={`Payslip ${detailTarget?.payslip_number}`}>
        {detailTarget && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                Basic Salary: <strong>${Number(detailTarget.basic_salary || 0).toLocaleString()}</strong>
              </div>
              <div>
                OT Pay: <strong>${Number(detailTarget.ot_pay || 0).toLocaleString()}</strong>
              </div>
              <div>
                OW Amount: <strong>${Number(detailTarget.ow_amount || 0).toLocaleString()}</strong>
              </div>
              <div>
                AW Amount: <strong>${Number(detailTarget.aw_amount || 0).toLocaleString()}</strong>
              </div>
              <div>
                CPF (Employee): <strong>${Number(detailTarget.cpf_employee || 0).toLocaleString()}</strong>
              </div>
              <div>
                CPF (Employer): <strong>${Number(detailTarget.cpf_employer || 0).toLocaleString()}</strong>
              </div>
              <div>
                SDL: <strong>${Number(detailTarget.sdl_amount || 0).toLocaleString()}</strong>
              </div>
              <div>
                Total Deductions: <strong>${Number(detailTarget.total_deductions || 0).toLocaleString()}</strong>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-blue-900 font-semibold">
              Net Pay: ${Number(detailTarget.net_pay || 0).toLocaleString()}
            </div>
            {detailComponents.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Components</h3>
                <ul className="divide-y">
                  {detailComponents.map((c: any) => (
                    <li key={c.id} className="py-1 flex justify-between">
                      <span>{c.component_name}</span>
                      <span>${Number(c.actual_amount).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
